import { useSuspenseQuery } from '@tanstack/react-query';

import { ConfigApiInstance } from '~src/main/api';
import { useSiteId } from '~src/main/hooks/useSiteId';

export function useSiteConfigQueryKey() {
  const siteId = useSiteId();

  const keys = {
    all: ['siteConfig', siteId] as const,
    get: () => [...keys.all, 'get'] as const,
  };

  return keys;
}

export function useSiteConfigQuery() {
  const queryKey = useSiteConfigQueryKey();
  const siteId = useSiteId();

  return useSuspenseQuery({
    queryFn: async () => {
      const { data } = await ConfigApiInstance.siteGet.configGetSite({ site_id: siteId });
      return data;
    },
    queryKey: [queryKey.get()],
  });
}

export function useBookingRulesQuery() {
  const { data, ...query } = useSiteConfigQuery();

  /**
   * Booking rules are defined under custom booking methods.
   *
   * If there's a rule configured, the custom booking method
   * contains one or more steps, hence the filter to exclude any
   * custom booking methods that does not have "steps" defined.
   */
  const customBookingMethods = (data?.site?.custom_booking_methods ?? []).filter(cbm => !!cbm.steps);

  return {
    data: customBookingMethods,
    ...query,
  };
}

export function useBookingRulesDetailsQuery({ method }: { method: string }) {
  const { data, ...query } = useBookingRulesQuery();

  /**
   * Booking rules are defined under custom booking methods, meaning there's no details endpoint.
   */
  const customBookingMethod = data.find(d => d.method === method);

  return {
    data: customBookingMethod,
    ...query,
  };
}
